// 必要なモジュールを読み込み
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js"; // 追加

export class ThreeApp {
  /**
   * カメラ定義のための定数
   */
  static CAMERA_PARAM = {
    fovy: 3,
    aspect: 1,
    near: 0.1,
    far: 500.0,
    position: new THREE.Vector3(0.0, 50.0, 200.0),
    lookAt: new THREE.Vector3(0.0, 0.0, 0.0),
  };
  /**
   * レンダラー定義のための定数
   */
  static RENDERER_PARAM = {
    clearColor: 0xffffff,
  };
  /**
   * マテリアル定義のための定数
   */
  static MATERIAL_PARAM_GLASS = {
    color: 0xffffff,
    transmission: 1.3,
    opacity: 1,
    metalness: 0,
    roughness: 0,
    ior: 1.6,
    thickness: 5,
    specularIntensity: 1,
    specularColor: 0xffffff,
    dispersion: 5,
  };

  canvas: HTMLCanvasElement; // 既存のcanvas要素
  renderer!: THREE.WebGLRenderer; // レンダラ
  scene!: THREE.Scene; // シーン
  camera!: THREE.PerspectiveCamera; // カメラ
  //今回ライトは不要なので削除している
  gltf!: any; // 読み込んだ glTF 格納用（低ポリ版）
  highQualityGltf!: any; // 高品質版 glTF 格納用
  matcapMaterial!: THREE.MeshMatcapMaterial; // マテリアル用テクスチャ
  backgroundTexture!: THREE.Texture; // 背景テクスチャ
  mixer!: THREE.AnimationMixer; // アニメーションミキサー
  actions!: THREE.AnimationAction[]; // アニメーションのアクション
  clock!: THREE.Clock; // アニメーション用のクロック
  animationFinished: boolean; // アニメーション終了フラグ
  animationStartTime: number; // アニメーション開始時間
  startAnimation: boolean; // アニメーション開始フラグ
  isHighQualityLoaded: boolean; // 高品質版読み込み完了フラグ
  isModelSwitched: boolean; // モデル切り替え完了フラグ
  mouseX: number; // マウスX座標
  mouseY: number; // マウスY座標
  targetMouseX: number; // 目標マウスX座標
  targetMouseY: number; // 目標マウスY座標
  gltfGroup!: THREE.Group; // GLBオブジェクト用のグループ
  glassSpheres!: THREE.Mesh[]; // ガラス球の配列

  // ローディング関連
  loadingElement!: HTMLElement; // ローディング表示要素
  loadingTextElement!: HTMLElement; // ローディングテキスト要素
  isLoading: boolean; // ローディング状態フラグ

  /**
   * コンストラクタ
   * @constructor
   * @param {HTMLCanvasElement} canvas - 既存のcanvas要素
   */
  constructor(canvas: HTMLCanvasElement) {
    // 既存のcanvas要素を保持
    this.canvas = canvas;

    // アニメーション終了フラグを初期化
    this.animationFinished = false;
    this.animationStartTime = 0;
    this.startAnimation = false;

    // マウス座標を初期化
    this.mouseX = 0;
    this.mouseY = 0;
    this.targetMouseX = 0;
    this.targetMouseY = 0;

    // ローディング状態を初期化
    this.isLoading = true;

    // モデル品質関連フラグを初期化
    this.isHighQualityLoaded = false;
    this.isModelSwitched = false;

    // this のバインド
    this.render = this.render.bind(this);

    // canvas内でのマウス移動イベントを追加
    this.canvas.addEventListener(
      "mousemove",
      (event) => {
        // canvas要素のサイズを取得
        const rect = this.canvas.getBoundingClientRect();
        // マウス座標を正規化（-1 から 1 の範囲）
        this.targetMouseX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.targetMouseY = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      },
      false
    );

    // canvas外に出た時は中央位置にリセット
    this.canvas.addEventListener(
      "mouseleave",
      () => {
        this.targetMouseX = 0;
        this.targetMouseY = 0;
      },
      false
    );

    // ウィンドウのリサイズを検出できるようにする
    window.addEventListener(
      "resize",
      () => {
        this.updateSize();
      },
      false
    );

    // restartButtonのイベントリスナーを追加
    const restartButton = document.querySelector("#restartButton") as HTMLButtonElement | null;
    if (restartButton) {
      restartButton.addEventListener("click", () => {
        if (this) {
          this.restartAnimation();
        }
      });
    } else {
    }
  }

  /**
   * サイズを更新する処理
   */
  updateSize() {
    const rect = this.canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  /**
   * ローディング表示の初期化
   */
  initLoading() {
    // ローディング要素を取得
    this.loadingElement = document.getElementById("balloonLoading")!;
    this.loadingTextElement =
      this.loadingElement?.querySelector(".loading-text") || this.loadingElement;
    if (this.loadingElement) {
      this.showLoading();
    }
  }

  /**
   * ローディング表示を表示
   */
  showLoading() {
    if (this.loadingElement) {
      this.loadingElement.style.display = "flex";
    }
    this.isLoading = true;
  }

  /**
   * ローディング表示を非表示
   */
  hideLoading() {
    if (this.loadingElement) {
      this.loadingElement.style.display = "none";
    }
    this.isLoading = false;
  }

  /**
   * レンダラーとシーンの初期化処理
   */
  initRenderer() {
    // canvas要素のサイズを取得
    const rect = this.canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    // レンダラー（既存のcanvasを使用）
    const color = new THREE.Color(ThreeApp.RENDERER_PARAM.clearColor);
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });
    this.renderer.setClearColor(color);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(width, height);

    // シーン
    this.scene = new THREE.Scene();

    // カメラ
    this.camera = new THREE.PerspectiveCamera(
      ThreeApp.CAMERA_PARAM.fovy,
      width / height,
      ThreeApp.CAMERA_PARAM.near,
      ThreeApp.CAMERA_PARAM.far
    );
    this.camera.position.copy(ThreeApp.CAMERA_PARAM.position);
    this.camera.lookAt(ThreeApp.CAMERA_PARAM.lookAt);

    // アニメーション時間管理のための Clock オブジェクトを生成しておく
    this.clock = new THREE.Clock();
  }

  /**
   * シーンの初期化処理（アセット読み込み後に実行）
   */
  initScene() {
    // 背景テクスチャを設定
    if (this.backgroundTexture) {
      this.scene.background = this.backgroundTexture;
    }

    // ガラス球
    this.glassSpheres = [];
    const glass_material = new THREE.MeshPhysicalMaterial({
      color: ThreeApp.MATERIAL_PARAM_GLASS.color,
      transmission: ThreeApp.MATERIAL_PARAM_GLASS.transmission,
      opacity: ThreeApp.MATERIAL_PARAM_GLASS.opacity,
      metalness: ThreeApp.MATERIAL_PARAM_GLASS.metalness,
      roughness: ThreeApp.MATERIAL_PARAM_GLASS.roughness,
      ior: ThreeApp.MATERIAL_PARAM_GLASS.ior,
      thickness: ThreeApp.MATERIAL_PARAM_GLASS.thickness,
      specularIntensity: ThreeApp.MATERIAL_PARAM_GLASS.specularIntensity,
      specularColor: ThreeApp.MATERIAL_PARAM_GLASS.specularColor,
      side: THREE.DoubleSide,
      dispersion: ThreeApp.MATERIAL_PARAM_GLASS.dispersion,
    });
    const sphereCount = 20;
    const spacing = 1;
    const startX = (-(sphereCount - 1) * spacing) / 2; // 中央揃えの開始位置

    for (let i = 0; i < sphereCount; i++) {
      const geometry = new THREE.SphereGeometry(2, 16, 16);
      const sphere = new THREE.Mesh(geometry, glass_material);
      sphere.scale.set(0.3, 0.3, 0.3);
      const x = startX + i * spacing;
      const y = 5;
      const z = 20;
      sphere.position.set(x, y, z);
      sphere.rotation.set(0, 0, 0);

      // アニメーション用のプロパティを追加（Y軸-20から+20の範囲で移動）
      sphere.userData = {
        originalY: 0, // 中央位置
        floatSpeed: 0.2, // 統一された速度
        floatHeight: 15, // Y軸-20から+20の範囲
        phaseOffset: (i / sphereCount) * Math.PI * 2, // 各球の位相オフセット（0から2π）
        rotationSpeed: {
          x: 0.01,
          y: 0.01,
          z: 0.01,
        },
      };

      this.glassSpheres.push(sphere);
      this.scene.add(sphere);
    }

    // GLBオブジェクト用のグループを作成
    if (this.gltf) {
      this.gltfGroup = new THREE.Group();
      this.gltfGroup.rotation.x = 2 * Math.PI;
      this.gltfGroup.add(this.gltf.scene);
      this.scene.add(this.gltfGroup);

      // GLBファイル内のすべてのメッシュのマテリアルを置き換える
      if (this.matcapMaterial) {
        this.gltf.scene.traverse((child: THREE.Mesh) => {
          if (child.isMesh) {
            child.material = this.matcapMaterial;
          }
        });
      }
    }
  }
  /**
   * 背景テクスチャ
   */
  loadBackgroundTexture() {
    return new Promise<void>((resolve) => {
      const loader = new THREE.TextureLoader();
      const canvasEl = document.getElementById("balloonCanvas") as HTMLCanvasElement | null;
      const assetsBase = (canvasEl?.dataset.assetsBase || "").replace(/\/$/, "");
      loader.load(
        `${assetsBase}/images/pages/work-006/bg.jpg`,
        (texture) => {
          this.backgroundTexture = texture;
          resolve();
        },
        undefined,
        (error) => {
          resolve();
        }
      );
    });
  }
  /**
   * マテリアル用テクスチャ
   */
  loadTexture() {
    return new Promise<void>((resolve) => {
      const canvasEl = document.getElementById("balloonCanvas") as HTMLCanvasElement | null;
      const assetsBase = (canvasEl?.dataset.assetsBase || "").replace(/\/$/, "");
      const loader = new THREE.TextureLoader();
      loader.load(
        `${assetsBase}/images/pages/work-006/m01.png`,
        (texture) => {
          this.matcapMaterial = new THREE.MeshMatcapMaterial({ matcap: texture });
          resolve();
        },
        undefined,
        (error) => {
          resolve();
        }
      );
    });
  }
  /**
   * 素材ロード（低ポリ版を先に読み込み、高品質版をバックグラウンドで読み込む）
   */
  load() {
    return new Promise<void>((resolve) => {
      const canvasEl = document.getElementById("balloonCanvas") as HTMLCanvasElement | null;
      const assetsBase = (canvasEl?.dataset.assetsBase || "").replace(/\/$/, "");

      // 低ポリ版のパス
      const lowPolyGltfPath = `${assetsBase}/images/pages/work-006/bln-7mb.glb`;

      // 低ポリ版を先に読み込み
      this.loadLowPolyModel(lowPolyGltfPath)
        .then(() => {
          // 他のテクスチャと並行読み込み
          Promise.all([this.loadBackgroundTexture(), this.loadTexture()])
            .then(() => {
              // シーンの初期化（低ポリ版で）
              this.initScene();

              // ローディング完了時にローディング表示を非表示
              this.hideLoading();

              resolve();

              // バックグラウンドで高品質版を読み込み開始
              this.loadHighQualityModel(`${assetsBase}/images/pages/work-006/bln.glb`);
            })
            .catch((error) => {
              // エラーの場合もローディングを非表示
              this.hideLoading();
              resolve();
            });
        })
        .catch((error) => {
          // エラーの場合もローディングを非表示
          this.hideLoading();
          resolve(); // エラーでも処理続行
        });
    });
  }

  /**
   * 低ポリ版モデルの読み込み
   */
  private loadLowPolyModel(gltfPath: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // DRACOLoader設定
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.6/");

      // GLTFLoader設定
      const loader = new GLTFLoader();
      loader.setDRACOLoader(dracoLoader);

      loader.load(
        gltfPath,
        (gltf) => {
          this.gltf = gltf;
          this.setupModelProperties(this.gltf);

          // DRACOLoaderをクリーンアップ
          dracoLoader.dispose();

          resolve();
        },
        (progress) => {},
        (error) => {
          dracoLoader.dispose();
          reject(error);
        }
      );
    });
  }

  /**
   * 高品質版モデルの読み込み（バックグラウンド）
   */
  private loadHighQualityModel(gltfPath: string): void {
    // DRACOLoader設定
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.6/");

    // GLTFLoader設定
    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    loader.load(
      gltfPath,
      (gltf) => {
        this.highQualityGltf = gltf;
        this.setupModelProperties(this.highQualityGltf);
        this.isHighQualityLoaded = true;

        // DRACOLoaderをクリーンアップ
        dracoLoader.dispose();

        // モデルの切り替えを実行
        this.switchToHighQualityModel();
      },
      (progress) => {},
      (error) => {
        console.error("高品質版GLTFファイル読み込みエラー:", error);
        dracoLoader.dispose();
      }
    );
  }

  /**
   * モデルの共通プロパティ設定
   */
  private setupModelProperties(gltf: any): void {
    gltf.scene.scale.set(8, 8, 8);
    gltf.scene.rotation.x = Math.PI / 2;

    // アニメーション設定（初回のみ）
    if (!this.mixer) {
      this.mixer = new THREE.AnimationMixer(gltf.scene);
      const animations = gltf.animations;
      this.actions = [];

      for (let i = 0; i < animations.length; ++i) {
        this.actions.push(this.mixer.clipAction(animations[i]));
        this.actions[i].setLoop(THREE.LoopRepeat, 1);
        this.actions[i].weight = 0.0;
      }

      this.actions[0].weight = 1.0;
      this.actions[0].setEffectiveTimeScale(0.5);
    }
  }

  /**
   * 高品質版モデルに切り替え
   */
  private switchToHighQualityModel(): void {
    if (
      !this.isHighQualityLoaded ||
      this.isModelSwitched ||
      !this.gltfGroup ||
      !this.highQualityGltf
    ) {
      return;
    }

    // 現在のアニメーション状態を保存
    const currentTime = this.mixer ? this.mixer.time : 0;
    const wasPlaying = this.startAnimation;

    // 古いモデルをシーンから削除
    this.gltfGroup.remove(this.gltf.scene);

    // 新しいモデルをシーンに追加
    this.gltfGroup.add(this.highQualityGltf.scene);

    // マテリアルを置き換える
    if (this.matcapMaterial) {
      this.highQualityGltf.scene.traverse((child: THREE.Mesh) => {
        if (child.isMesh) {
          child.material = this.matcapMaterial;
        }
      });
    }

    // アニメーションミキサーを更新
    this.mixer = new THREE.AnimationMixer(this.highQualityGltf.scene);
    const animations = this.highQualityGltf.animations;
    this.actions = [];

    for (let i = 0; i < animations.length; ++i) {
      this.actions.push(this.mixer.clipAction(animations[i]));
      this.actions[i].setLoop(THREE.LoopRepeat, 1);
      this.actions[i].weight = 0.0;
    }

    this.actions[0].weight = 1.0;
    this.actions[0].setEffectiveTimeScale(0.5);

    // アニメーション状態を復元
    if (wasPlaying) {
      this.mixer.setTime(currentTime);
      this.actions[0].play();
    }

    // 古いGLTFを置き換え
    this.gltf = this.highQualityGltf;

    this.isModelSwitched = true;
  }

  /**
   * 描画処理
   */
  render() {
    // 恒常ループ
    requestAnimationFrame(this.render);

    const delta = this.clock.getDelta();
    const elapsedTime = this.clock.getElapsedTime();

    // マウス座標をスムーズに補間
    const lerpSpeed = 0.1; // 補間速度（0-1の範囲、大きいほど速い）
    this.mouseX += (this.targetMouseX - this.mouseX) * lerpSpeed;
    this.mouseY += (this.targetMouseY - this.mouseY) * lerpSpeed;

    // ガラス球のアニメーション
    this.glassSpheres.forEach((sphere) => {
      this.GlassBallAnimation(sphere, elapsedTime);
    });

    // 1.5秒後にアニメーションを開始（restart時は即座に開始）
    const animationDelay = this.animationStartTime === 0 ? 1.5 : 0; // restart時は0.1秒の短い遅延
    if (!this.startAnimation && elapsedTime >= animationDelay) {
      this.startAnimation = true;
      this.animationStartTime = elapsedTime;
      this.actions[0].play();
    }
    // アニメーション終了（アニメーション開始から2.5秒後）
    if (
      this.actions &&
      this.actions[0] &&
      this.startAnimation &&
      elapsedTime - this.animationStartTime >= 2.5 &&
      !this.animationFinished
    ) {
      this.animationFinished = true;
      this.actions[0].paused = true;
    }
    // アニメーションが終了していない場合のみミキサーを更新
    if (!this.animationFinished) {
      this.mixer.update(delta);
    }

    // 上下にふわふわ動くアニメーション
    if (this.gltfGroup) {
      this.gltfGroupFloatAnimation(elapsedTime);
    }

    // 通常のレンダリング
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * ガラス球のアニメーション
   */
  GlassBallAnimation(sphere: THREE.Mesh, elapsedTime: number) {
    // 浮遊アニメーション（位相オフセットを適用）
    sphere.position.y =
      sphere.userData.originalY +
      Math.sin(elapsedTime * sphere.userData.floatSpeed + sphere.userData.phaseOffset) *
        sphere.userData.floatHeight;

    // 回転アニメーション
    sphere.rotation.x += sphere.userData.rotationSpeed.x;
    sphere.rotation.y += sphere.userData.rotationSpeed.y;
    sphere.rotation.z += sphere.userData.rotationSpeed.z;
  }
  /**
   * gltfGroupのアニメーション
   */
  gltfGroupFloatAnimation(elapsedTime: number) {
    // ふわふわ上下に動くアニメーション
    const floatSpeed = 1.5; // 浮遊速度
    const floatHeight = 0.3; // 浮遊の高さ
    this.gltfGroup.position.y = Math.sin(elapsedTime * floatSpeed) * floatHeight;

    // マウスの方向に少し向ける（グループのY軸とZ軸回転を使用）
    const lookAtSpeed = 0.05; // 向きの変化速度
    const maxRotation = 0.5; // 最大回転角度（ラジアン）
    const currentRotationY = this.gltfGroup.rotation.y;
    const currentRotationZ = this.gltfGroup.rotation.z;
    const targetRotationY = this.mouseX * maxRotation;
    const targetRotationZ = this.mouseY * maxRotation * 0.5; // Z軸の回転は控えめに
    this.gltfGroup.rotation.y += (targetRotationY - currentRotationY) * lookAtSpeed;
    this.gltfGroup.rotation.z += (targetRotationZ - currentRotationZ) * lookAtSpeed;
  }

  /**
   * アニメーションを再開始する
   */
  restartAnimation() {
    if (!this.actions || !this.actions[0]) {
      return;
    }

    // アニメーション状態をリセット
    this.animationFinished = false;
    this.startAnimation = false;
    this.animationStartTime = 0;

    // アニメーションを停止して初期状態に戻す
    this.actions[0].stop();
    this.actions[0].reset();
    this.actions[0].paused = false;
    this.actions[0].weight = 1.0;
    this.actions[0].setEffectiveTimeScale(0.5);

    // Clockをリセット（より確実にリセット）
    this.clock.stop();
    this.clock.start();
  }
}
